import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import { SessionContext } from "../context/sessionContext";
import SurveyFlowHeader from "../components/SurveyFlowHeader";
import {
  patchSurveySpecs,
  savePendingGenerateDraft,
  markStepReached,
  getMaxStepReached,
} from "../utils/surveyFlowStorage";

const surveyTypes = [
  {
    id: "csat",
    title: "Customer Satisfaction",
    desc: "Understand how happy your customers are with your product or service.",
  },
  {
    id: "product",
    title: "Product Research",
    desc: "Test new ideas, features, or concepts before launching.",
  },
  {
    id: "market",
    title: "Market Assessment",
    desc: "Explore your target audience, needs, and competitors.",
  },
  {
    id: "tracking",
    title: "Tracking Study",
    desc: "Monitor changes in opinions or behaviors over time.",
  },
];

const STORAGE_KEY = "surveySpecifications";

function autoResize(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}
function collapseNewlines(value) {
  return value
    .replace(/[\r\n]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function TagField({ label, placeholder, value, onChange, rows = 3 }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    autoResize(textareaRef.current);
  }, [value]);

  const handleChange = (e) => {
    onChange(e.target.value);
    autoResize(e.target);
  };

  const handleBlur = () => {
    const collapsed = collapseNewlines(value);
    if (collapsed !== value) {
      onChange(collapsed);
    }
  };

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <textarea
        ref={textareaRef}
        className="survey-form-textarea"
        placeholder={placeholder}
        value={value}
        rows={rows}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{
          resize: "none",
          overflow: "hidden",
          minHeight: `${rows * 1.5}em`,
        }}
      />
    </div>
  );
}

function AutoInput({ label, placeholder, value, onChange }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    autoResize(textareaRef.current);
  }, [value]);

  const handleChange = (e) => {
    onChange(e.target.value);
    autoResize(e.target);
  };

  const handleBlur = () => {
    const collapsed = collapseNewlines(value);
    if (collapsed !== value) {
      onChange(collapsed);
    }
  };

  return (
    <div className="form-group" style={{ marginTop: "1.75rem" }}>
      <label className="form-label">{label}</label>

      <textarea
        ref={textareaRef}
        className="survey-form-input"
        placeholder={placeholder}
        value={value}
        rows={1}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{
          resize: "none",
          overflow: "hidden",
          minHeight: "2.8em",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      />
    </div>
  );
}
export default function AddSurveySpecifications() {
  const saved = (() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [coreQuestion, setCoreQuestion] = useState(saved.coreQuestions || "");
  const [businessObjective, setBusinessObjective] = useState(
    saved.businessContext || "",
  );
  const [targetAudience, setTargetAudience] = useState(
    saved.targetAudience || "",
  );
  const [keyConcepts, setKeyConcepts] = useState(saved.keyConcepts || "");
  const [selectedType, setSelectedType] = useState(saved.selectedType || null);

  const [maxStepReached] = useState(() => {
    markStepReached("specs");
    return getMaxStepReached() || "specs";
  });

  const navigate = useNavigate();
  const { isAuthenticated, getUserId } = useContext(SessionContext);

  const canContinue =
    coreQuestion.trim() &&
    businessObjective.trim() &&
    targetAudience.trim() &&
    selectedType;

  const persist = useCallback((patch) => {
    patchSurveySpecs(patch);
  }, []);

  const makeHandler = (setter, key) => (val) => {
    setter(val);
    persist({ [key]: val });
  };

  const handleTypeSelect = (id) => {
    setSelectedType(id);
    persist({ selectedType: id });
  };

  const handleContinue = () => {
    if (!canContinue) return;

    if (isAuthenticated) {
      savePendingGenerateDraft(getUserId?.(), "robustness", {
        specs: {
          coreQuestions: coreQuestion,
          businessContext: businessObjective,
          targetAudience,
          keyConcepts,
          selectedType,
        },
      });
    }

    navigate("/survey-robustness", {
      state: {
        coreQuestions: coreQuestion,
        businessContext: businessObjective,
        targetAudience,
        keyConcepts,
        selectedType,
      },
    });
  };

  return (
    <div className="page-bg">
      <SurveyFlowHeader
        flow="generated"
        activeStep="specs"
        isAuthenticated={isAuthenticated}
        maxStepReached={maxStepReached}
      />
      <div className="content-center">
        <div className="survey-container">
          <h1 className="survey-page-title">Add Survey Specifications</h1>
          <br />
          <TagField
            label="What must this research answer?"
            placeholder="State the purpose of this survey and list the 2–4 critical strategic questions it must resolve"
            value={coreQuestion}
            onChange={makeHandler(setCoreQuestion, "coreQuestions")}
          />
          <TagField
            label="Describe your company, product, or service"
            placeholder="Include your industry, what you offer, who you serve, and your stage (startup, established, new entrant, etc.)"
            value={businessObjective}
            onChange={makeHandler(setBusinessObjective, "businessContext")}
          />
          <TagField
            label="Describe your target audience"
            placeholder="Who are they, their role, and their relationship to your category or product"
            value={targetAudience}
            onChange={makeHandler(setTargetAudience, "targetAudience")}
          />
          <TagField
            label="What should this survey test or measure?"
            placeholder="List specific products, features, competitors, pricing sensitivity, risks, or value propositions to probe"
            value={keyConcepts}
            onChange={makeHandler(setKeyConcepts, "keyConcepts")}
          />
          <div className="survey-card-radio">
            <p className="survey-card-radio__heading">Select Survey Type</p>
            {surveyTypes.map((type) => (
              <React.Fragment key={type.id}>
                <div
                  className={`radio-option${
                    selectedType === type.id ? " radio-option--selected" : ""
                  }`}
                  onClick={() => handleTypeSelect(type.id)}
                  role="radio"
                  aria-checked={selectedType === type.id}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTypeSelect(type.id);
                  }}
                >
                  <span
                    className={`radio-circle${
                      selectedType === type.id ? " radio-circle--selected" : ""
                    }`}
                  >
                    {selectedType === type.id && <span className="radio-dot" />}
                  </span>
                  <span>
                    <span className="radio-option__title">{type.title}</span>
                    <br />
                    <span className="radio-option__desc">{type.desc}</span>
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="action-bar action-bar--right">
            <button
              className="btn-primary"
              onClick={handleContinue}
              disabled={!canContinue}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
